import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  Divider,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InfoIcon from "@mui/icons-material/Info";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import '../../Styles/AccountingStyles.css'
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { useDispatch } from "react-redux";
import { GetPerformanceReturns, GetInvestorStatementFile, GetFundInvestorStatement } from "../../Redux/Actions/strategyService";

const FundDetailsTab = ({ fund, id, fundId, investors = [] }) => {

  const [timeFrame, setTimeFrame] = useState("1M");
  const [openTimelineModal, setOpenTimelineModal] = useState(false);
  const [timelineType, setTimelineType] = useState('single');
  const [selectedReport, setSelectedReport] = useState('');
  const [selectedYear, setSelectedYear] = useState('2018');
  const [selectedSingleMonth, setSelectedSingleMonth] = useState('');
  
  // For the range option
  const [rangeStartAnchorEl, setRangeStartAnchorEl] = useState(null);
  const [rangeEndAnchorEl, setRangeEndAnchorEl] = useState(null);
  const [rangeStartYear, setRangeStartYear] = useState('2018');
  const [rangeEndYear, setRangeEndYear] = useState('2018');
  const [selectedRangeStart, setSelectedRangeStart] = useState('');
  const [selectedRangeEnd, setSelectedRangeEnd] = useState('');
  
  // For custom option
  const [selectedMonthYears, setSelectedMonthYears] = useState([]);

  // For single option
  const [singleAnchorEl, setSingleAnchorEl] = useState(null);
  const [singleYear, setSingleYear] = useState('2018');

  const [selectedInvestorId, setSelectedInvestorId] = useState("");

  const dispatch = useDispatch();

  const handleTimeFrameChange = (event, newValue) => {
    if (newValue !== null) {
      setTimeFrame(newValue);
    }
  };

  const handleOpenTimelineModal = (reportType) => {
    setSelectedReport(reportType);
    setOpenTimelineModal(true);
  };

  const handleCloseTimelineModal = () => {
    setOpenTimelineModal(false);
  };

  const handleTimelineTypeChange = (event) => {
    setTimelineType(event.target.value);
  };

  // Single option handlers
  const handleSingleClick = (event) => {
    setSingleAnchorEl(event.currentTarget);
  };

  const handleSingleClose = () => {
    setSingleAnchorEl(null);
  };

  const handleSingleYearChange = (direction) => {
    const newYear = parseInt(singleYear) + direction;
    setSingleYear(newYear.toString());
  };

  const handleSingleSelect = (month) => {
    setSelectedSingleMonth(`${month} ${singleYear}`);
    handleSingleClose();
  };

  // Range option handlers
  const handleRangeStartClick = (event) => {
    setRangeStartAnchorEl(event.currentTarget);
  };

  const handleRangeEndClick = (event) => {
    setRangeEndAnchorEl(event.currentTarget);
  };

  const handleRangeStartClose = () => {
    setRangeStartAnchorEl(null);
  };

  const handleRangeEndClose = () => {
    setRangeEndAnchorEl(null);
  };

  const handleRangeStartYearChange = (direction) => {
    const newYear = parseInt(rangeStartYear) + direction;
    setRangeStartYear(newYear.toString());
  };

  const handleRangeEndYearChange = (direction) => {
    const newYear = parseInt(rangeEndYear) + direction;
    setRangeEndYear(newYear.toString());
  };

  const handleRangeStartSelect = (month) => {
    setSelectedRangeStart(`${month} ${rangeStartYear}`);
    handleRangeStartClose();
  };

  const handleRangeEndSelect = (month) => {
    // Convert selected dates to comparable format
    const startDate = selectedRangeStart ? new Date(`${selectedRangeStart} 1, 2000`) : null;
    const endDate = new Date(`${month} 1, ${rangeEndYear}`);
    
    // If start date exists, check if end date is after it
    if (startDate) {
      const startYear = parseInt(rangeStartYear);
      const endYear = parseInt(rangeEndYear);
      
      // Compare years first
      if (endYear < startYear) {
        return; // Invalid selection
      }
      
      // If same year, compare months
      if (endYear === startYear && endDate <= startDate) {
        return; // Invalid selection
      }
    }

    setSelectedRangeEnd(`${month} ${rangeEndYear}`);
    handleRangeEndClose();
  };

  // Add this helper function to determine if a month should be disabled
  const isMonthDisabled = (month) => {
    if (!selectedRangeStart) return false;
    
    const [startMonth, startYear] = selectedRangeStart.split(' ');
    const startDate = new Date(`${startMonth} 1, ${startYear}`);
    const currentDate = new Date(`${month} 1, ${rangeEndYear}`);
    
    return (
      parseInt(rangeEndYear) < parseInt(startYear) || 
      (parseInt(rangeEndYear) === parseInt(startYear) && currentDate <= startDate)
    );
  };

  // Custom option handlers
  const handleYearChange = (newYear) => {
    setSelectedYear(newYear);
  };

  const isMonthSelected = (month) => {
    return selectedMonthYears.some(item => 
      item.month === month && item.year === selectedYear
    );
  };

  const handleMonthSelect = (month) => {
    const exists = selectedMonthYears.some(
      item => item.month === month && item.year === selectedYear
    );
    
    if (exists) {
      setSelectedMonthYears(selectedMonthYears.filter(
        item => !(item.month === month && item.year === selectedYear)
      ));
    } else {
      setSelectedMonthYears([...selectedMonthYears, { month, year: selectedYear }]);
    }
  };

  const handleSelectAllMonths = (event) => {
    const allMonths = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    
    const filteredSelections = selectedMonthYears.filter(item => item.year !== selectedYear);
    
    if (event.target.checked) {
      const newMonths = allMonths.map(month => ({ month, year: selectedYear }));
      setSelectedMonthYears([...filteredSelections, ...newMonths]);
    } else {
      setSelectedMonthYears(filteredSelections);
    }
  };

  const handleRemoveMonthYear = (monthToRemove, yearToRemove) => {
    setSelectedMonthYears(selectedMonthYears.filter(
      item => !(item.month === monthToRemove && item.year === yearToRemove)
    ));
  };

  const areAllMonthsSelected = () => {
    const monthsForCurrentYear = selectedMonthYears.filter(item => item.year === selectedYear);
    return monthsForCurrentYear.length === 12;
  };

  const areSomeMonthsSelected = () => {
    const monthsForCurrentYear = selectedMonthYears.filter(item => item.year === selectedYear);
    return monthsForCurrentYear.length > 0 && monthsForCurrentYear.length < 12;
  };

  // General handlers
  const handleDownloadReport = async () => {
    if (selectedReport === 'investor') {
      // Validate selection
      if (!selectedSingleMonth) {
        // Optionally show an error message
        return;
      }

      // Parse month and year
      const [monthName, year] = selectedSingleMonth.split(' ');
      const monthIndex = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ].indexOf(monthName) + 1;
      const monthStr = monthIndex < 10 ? `0${monthIndex}` : `${monthIndex}`;

      try {
        const response = await dispatch(GetFundInvestorStatement({
          id,
          fundId,
          year,
          month: monthStr
        })).unwrap();

        // Handle .zip file download
        if (response && response.blob) {
          const url = window.URL.createObjectURL(response.blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = response.filename || 'InvestorStatement.zip';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        // Handle error (show notification, etc.)
      }
      handleCloseTimelineModal();
      return;
    }

    if (selectedReport === 'portfolio') {
      if (!selectedInvestorId || !selectedSingleMonth) return;
      const [monthName, year] = selectedSingleMonth.split(' ');
      const monthIndex = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ].indexOf(monthName) + 1;
      const monthStr = monthIndex < 10 ? `0${monthIndex}` : `${monthIndex}`;

      try {
        const response = await dispatch(GetInvestorStatementFile({
          id,
          investorId: selectedInvestorId,
          year,
          month: monthStr
        })).unwrap();

        // Handle file download (blob)
        if (response && response.blob) {
          const url = window.URL.createObjectURL(response.blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = response.filename || 'PortfolioReport.zip';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        // Handle error
      }
      handleCloseTimelineModal();
      return;
    }

    handleCloseTimelineModal();
  };

  const isDownloadButtonDisabled = () => {
    if (timelineType === 'single') return !selectedSingleMonth;
    if (timelineType === 'range') return !selectedRangeStart || !selectedRangeEnd;
    if (timelineType === 'custom') return selectedMonthYears.length === 0;
    return true;
  };

  // For chart data
  const performanceData = [
    { month: 'Jan', value: 0 },
    { month: 'Feb', value: 300 },
    { month: 'Mar', value: 150 },
    { month: 'Apr', value: 1100 },
    { month: 'Mai', value: 600 },
    { month: 'Jun', value: 850 },
    { month: 'Jul', value: 0 }
  ];

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                 'July', 'August', 'September', 'October', 'November', 'December'];

  // Handler for investor selection
  const handleInvestorChange = (event) => {
    setSelectedInvestorId(event.target.value);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* First row */}
        <Grid item xs={12} md={8}>
          <Paper className="performance-container paper">
            <Box className="performance-header">
              <Typography className="section-title">Performance</Typography>
              <IconButton size="small">
                <AspectRatioIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Box className="performance-metrics">
              <Box className="metric-container">
                <Typography variant="body2" color="text.secondary" className="text-small">Net ROR: Fund Level</Typography>
                <Box className="metric-value">
                  <Typography className="metric-number">230.99%</Typography>
                  <KeyboardArrowUpIcon fontSize="small" className="metric-positive" />
                </Box>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary" className="text-small">AUM</Typography>
                <Typography className="metric-number">
                  <span className="metric-positive">$11,000,000.21</span>
                </Typography>
              </Box>
            </Box>
            
            <Box className="chart-container">
              <Box className="time-frame-container">
                <ToggleButtonGroup
                  value={timeFrame}
                  exclusive
                  onChange={handleTimeFrameChange}
                  aria-label="time frame"
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      margin: '0 4px',
                      padding: '4px 16px',
                      textTransform: 'none',
                      color: 'black',
                      fontWeight: '500',
                      '&.Mui-selected': {
                        backgroundColor: '#D3DBF859',
                        color: 'blue',
                        border: '1px solid blue',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        }
                      },
                      '&:first-of-type': {
                        marginLeft: 0
                      }
                    }
                  }}
                >
                  <ToggleButton value="1M">1M</ToggleButton>
                  <ToggleButton value="3M">3M</ToggleButton>
                  <ToggleButton value="1Y">1Y</ToggleButton>
                  <ToggleButton value="ITD">ITD</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              {/* Performance Chart using Recharts */}
              <Box className="chart-area">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={performanceData}
                    margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="month" 
                      axisLine={{ stroke: '#e0e0e0' }}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: '#666' }}
                    />
                    <YAxis 
                      domain={[-100, 1500]}
                      ticks={[-100, 0, 300, 600, 900, 1200, 1500]}
                      axisLine={{ stroke: '#e0e0e0' }}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: '#666' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "#1976d2" }}
                      activeDot={{ r: 5 }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper className="asset-details-container paper">
            <Typography className="section-title asset-details-header">Asset Details</Typography>
            
            <Box className="mb-2">
              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" className="text-secondary">Asset</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" align="right" className="text-secondary">% Allocation</Typography>
                </Grid>
              </Grid>
            </Box>
            
            <Box className="asset-details-scrollable">
              {[
                { asset: "BTC", allocation: 75 },
                { asset: "SOL", allocation: 10 },
                { asset: "ETH", allocation: 2 },
                { asset: "BNB", allocation: 2 },
                { asset: "ADA", allocation: 2 },
                { asset: "XRP", allocation: 2 },
                { asset: "DOT", allocation: 2 },
                { asset: "DOGE", allocation: 1 },
                { asset: "AVAX", allocation: 1 },
                { asset: "MATIC", allocation: 1 },
                { asset: "LINK", allocation: 1 },
                { asset: "UNI", allocation: 1 }
              ].map((asset, index) => (
                <Box key={index} className="asset-item">
                  <Grid container alignItems="center">
                    <Grid item xs={6}>
                      <Typography className="text-secondary">{asset.asset}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right" className="text-secondary">{asset.allocation}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper className="reports-container paper">
            <Typography className="section-title">Download Reports</Typography>
            
            <Box className="reports-scrollable">
              {[
                { name: "Investor Reports", type: "investor" },
                { name: "Portfolio Reports", type: "portfolio" },
                { name: "Daily Reports", type: "daily" },
                { name: "Monthly Reports", type: "monthly" }
              ].map((report, index) => (
                <Box 
                  key={index} 
                  className="report-item"
                  style={{ borderBottom: index < 3 ? "1px solid #e0e0e0" : "none" }}
                >
                  <Typography className="text-secondary">{report.name}</Typography>
                  <IconButton 
                    color="primary" 
                    className="download-button"
                    onClick={() => handleOpenTimelineModal(report.type)}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Paper className="vami-container paper">
            <Box className="vami-header">
              <Typography className="section-title">Vami Chart</Typography>
              <IconButton size="small">
                <AspectRatioIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Box className="mb-2">
              <Typography variant="body2" color="text.secondary" className="text-small">Net ROR: Fund Level</Typography>
            </Box>
            
            <Box className="time-frame-container" sx={{mt:-4}}>
                <ToggleButtonGroup
                  value={timeFrame}
                  exclusive
                  onChange={handleTimeFrameChange}
                  aria-label="time frame"
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      margin: '0 4px',
                      padding: '4px 16px',
                      textTransform: 'none',
                      color: 'black',
                      fontWeight: '500',
                      '&.Mui-selected': {
                        backgroundColor: '#D3DBF859',
                        color: 'blue',
                        border: '1px solid blue',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        }
                      },
                      '&:first-of-type': {
                        marginLeft: 0
                      }
                    }
                  }}
                >
                  <ToggleButton value="1M">1M</ToggleButton>
                  <ToggleButton value="3M">3M</ToggleButton>
                  <ToggleButton value="1Y">1Y</ToggleButton>
                  <ToggleButton value="ITD">ITD</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            
            
            <Box className="vami-chart-area">
              {/* This would be replaced with actual chart implementation */}
              <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Vami Chart Implementation</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Timeline Selection Modal */}
      <Dialog 
        open={openTimelineModal} 
        onClose={handleCloseTimelineModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          pb: 1
        }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'normal' }}>
            Select Timeline
          </Typography>
          <IconButton onClick={handleCloseTimelineModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select month(s) to download report for
          </Typography>

          <RadioGroup
            value={timelineType}
            onChange={handleTimelineTypeChange}
            sx={{
              flexDirection: 'row',
              mb: 2
            }}
          >
            <FormControlLabel 
              value="single" 
              control={<Radio size="small" sx={{ color: '#1976d2' }} />} 
              label="Single" 
            />
            <FormControlLabel 
              value="range" 
              control={<Radio size="small" sx={{ color: '#1976d2' }} />} 
              label="Range" 
            />
            <FormControlLabel 
              value="custom" 
              control={<Radio size="small" sx={{ color: '#1976d2' }} />} 
              label="Custom" 
            />
          </RadioGroup>

          {/* SINGLE OPTION */}
          {timelineType === 'single' && (
            <Box>
              {/* Show investor dropdown only for portfolio report */}
              {selectedReport === 'portfolio' && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="investor-select-label">Select Investor</InputLabel>
                  <Select
                    labelId="investor-select-label"
                    value={selectedInvestorId}
                    label="Select Investor"
                    onChange={handleInvestorChange}
                  >
                    {investors.map((inv) => (
                      <MenuItem key={inv.InvestorID} value={inv.InvestorID}>
                        {inv.InvestorName} ({inv.InvestorNumber})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {/* Custom dropdown field */}
              <Box 
                onClick={handleSingleClick}
                sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.23)', 
                  borderRadius: '4px', 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  }
                }}
              >
                <Typography color={selectedSingleMonth ? 'text.primary' : 'text.secondary'}>
                  {selectedSingleMonth || 'MM/YYYY'}
                </Typography>
                <Box sx={{ color: 'action.active' }}>▼</Box>
              </Box>

              {/* Month selection popover */}
              <Popover
                open={Boolean(singleAnchorEl)}
                anchorEl={singleAnchorEl}
                onClose={handleSingleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: { 
                    width: singleAnchorEl ? singleAnchorEl.clientWidth : 'auto',
                    mt: 0.5,
                    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                <Box>
                  {/* Year navigation */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      p: 1
                    }}
                  >
                    <IconButton size="small" onClick={() => handleSingleYearChange(-1)}>
                      <KeyboardArrowLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontWeight: 'medium' }}>{singleYear}</Typography>
                    <IconButton size="small" onClick={() => handleSingleYearChange(1)}>
                      <KeyboardArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {/* Months list */}
                  <List sx={{ 
                    pt: 0, 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#1976d2',
                      borderRadius: '3px',
                    }
                  }}>
                    {months.map((month) => {
                      const isSelected = selectedSingleMonth === `${month} ${singleYear}`;
                      return (
                        <ListItem 
                          key={month} 
                          disablePadding 
                          sx={{
                            bgcolor: isSelected ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                          }}
                        >
                          <ListItemButton 
                            onClick={() => handleSingleSelect(month)}
                            sx={{ py: 1.5 }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              width: '100%',
                              justifyContent: 'space-between'
                            }}>
                              <Typography>{month}</Typography>
                              {isSelected && <CheckIcon fontSize="small" sx={{ color: '#1976d2' }} />}
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Popover>
            </Box>
          )}

          {/* RANGE OPTION */}
          {timelineType === 'range' && (
            <>
              <Typography sx={{ mb: 1, fontWeight: 'medium' }}>Starting Month:</Typography>
              <Box 
                onClick={handleRangeStartClick}
                sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.23)', 
                  borderRadius: '4px', 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  cursor: 'pointer',
                  mb: 2,
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  }
                }}
              >
                <Typography color={selectedRangeStart ? 'text.primary' : 'text.secondary'}>
                  {selectedRangeStart || 'MM/YYYY'}
                </Typography>
                <Box sx={{ color: 'action.active' }}>▼</Box>
              </Box>
              
              <Typography variant="body2" sx={{ mb: 1 }}>Ending Month:</Typography>
              <Box 
                sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.23)', 
                  borderRadius: '4px', 
                  p: 1.5, 
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onClick={handleRangeEndClick}
              >
                <Typography color={selectedRangeEnd ? 'text.primary' : 'text.secondary'}>
                  {selectedRangeEnd || 'MM/YYYY'}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem' }}>▼</Typography>
              </Box>
              
              {/* Starting Month Dropdown */}
              <Popover
                open={Boolean(rangeStartAnchorEl)}
                anchorEl={rangeStartAnchorEl}
                onClose={handleRangeStartClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: { 
                    width: rangeStartAnchorEl ? rangeStartAnchorEl.clientWidth : 'auto',
                    mt: 0.5,
                    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                <Box>
                  {/* Year navigation */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      p: 1
                    }}
                  >
                    <IconButton size="small" onClick={() => handleRangeStartYearChange(-1)}>
                      <KeyboardArrowLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontWeight: 'medium' }}>{rangeStartYear}</Typography>
                    <IconButton size="small" onClick={() => handleRangeStartYearChange(1)}>
                      <KeyboardArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {/* Months list */}
                  <List sx={{ 
                    pt: 0, 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#1976d2',
                      borderRadius: '3px',
                    }
                  }}>
                    {months.map((month) => {
                      const isSelected = selectedRangeStart === `${month} ${rangeStartYear}`;
                      return (
                        <ListItem 
                          key={month} 
                          disablePadding 
                          sx={{
                            bgcolor: isSelected ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                          }}
                        >
                          <ListItemButton 
                            onClick={() => handleRangeStartSelect(month)}
                            sx={{ py: 1.5 }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              width: '100%',
                              justifyContent: 'space-between'
                            }}>
                              <Typography>{month}</Typography>
                              {isSelected && <CheckIcon fontSize="small" sx={{ color: '#1976d2' }} />}
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Popover>
              
              {/* Ending Month Dropdown */}
              <Popover
                open={Boolean(rangeEndAnchorEl)}
                anchorEl={rangeEndAnchorEl}
                onClose={handleRangeEndClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: { 
                    width: rangeEndAnchorEl ? rangeEndAnchorEl.clientWidth : 'auto',
                    mt: 0.5,
                    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                <Box>
                  {/* Year navigation */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      p: 1
                    }}
                  >
                    <IconButton size="small" onClick={() => handleRangeEndYearChange(-1)}>
                      <KeyboardArrowLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontWeight: 'medium' }}>{rangeEndYear}</Typography>
                    <IconButton size="small" onClick={() => handleRangeEndYearChange(1)}>
                      <KeyboardArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {/* Months list */}
                  <List sx={{ 
                    pt: 0, 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#1976d2',
                      borderRadius: '3px',
                    }
                  }}>
                    {months.map((month) => {
                      const isSelected = selectedRangeEnd === `${month} ${rangeEndYear}`;
                      const disabled = isMonthDisabled(month);
                      return (
                        <ListItem 
                          key={month} 
                          disablePadding 
                          sx={{
                            bgcolor: isSelected ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                            opacity: disabled ? 0.5 : 1,
                            pointerEvents: disabled ? 'none' : 'auto'
                          }}
                        >
                          <ListItemButton 
                            onClick={() => handleRangeEndSelect(month)}
                            sx={{ py: 1.5 }}
                            disabled={disabled}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              width: '100%',
                              justifyContent: 'space-between'
                            }}>
                              <Typography>{month}</Typography>
                              {isSelected && <CheckIcon fontSize="small" sx={{ color: '#1976d2' }} />}
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Popover>
            </>
          )}

          {/* CUSTOM OPTION */}
          {timelineType === 'custom' && (
            <Box>
              {/* Display selected month-year combinations as chips */}
              <Box 
                sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.23)', 
                  borderRadius: '4px', 
                  p: 1,
                  mb: 2,
                  minHeight: '42px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1
                }}
              >
                {selectedMonthYears.map(({month, year}) => (
                  <Box 
                    key={`${month}-${year}`}
                    sx={{
                      bgcolor: '#e3f2fd',
                      borderRadius: '16px',
                      px: 1,
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#1976d2' }}>
                      {month} {year}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => handleRemoveMonthYear(month, year)}
                      sx={{ 
                        p: 0.2,
                        color: '#1976d2',
                        '&:hover': {
                          bgcolor: 'rgba(25, 118, 210, 0.1)'
                        } 
                      }}
                    >
                      <CloseIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              {/* Year navigation */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 2,
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                paddingBottom: 1
              }}>
                <IconButton 
                  onClick={() => handleYearChange((parseInt(selectedYear) - 1).toString())}
                  size="small"
                >
                  <Box sx={{ fontSize: '1.2rem' }}>‹</Box>
                </IconButton>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#1976d2', 
                    fontWeight: 'medium'
                  }}
                >
                  {selectedYear}
                </Typography>
                
                <IconButton 
                  onClick={() => handleYearChange((parseInt(selectedYear) + 1).toString())}
                  size="small"
                >
                  <Box sx={{ fontSize: '1.2rem' }}>›</Box>
                </IconButton>
              </Box>

              {/* Month selection with checkboxes for current year */}
              <Box 
                sx={{ 
                  maxHeight: '250px', 
                  overflowY: 'auto',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#1976d2',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                  }
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.5,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                }}>
                  <Checkbox 
                    checked={areAllMonthsSelected()}
                    indeterminate={areSomeMonthsSelected()}
                    onChange={handleSelectAllMonths}
                    size="small"
                    sx={{ p: 0, mr: 1.5 }}
                  />
                  <Typography>Select All</Typography>
                </Box>
                
                {months.map((month) => (
                  <Box 
                    key={month} 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                    }}
                  >
                    <Checkbox 
                      checked={isMonthSelected(month)}
                      onChange={() => handleMonthSelect(month)}
                      size="small"
                      sx={{ 
                        p: 0, 
                        mr: 1.5,
                        color: isMonthSelected(month) ? '#1976d2' : undefined
                      }}
                    />
                    <Typography>{month}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleCloseTimelineModal} 
            variant="outlined"
            sx={{ 
              borderColor: 'rgba(0, 0, 0, 0.23)',
              color: 'text.primary',
              textTransform: 'none',
              mr: 1,
              borderRadius: '4px'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadReport}
            variant="contained"
            disabled={isDownloadButtonDisabled()}
            sx={{ 
              bgcolor: '#1976d2',
              textTransform: 'none',
              borderRadius: '4px',
              '&.Mui-disabled': {
                bgcolor: 'rgba(25, 118, 210, 0.5)',
                color: 'white'
              }
            }}
          >
            Download Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FundDetailsTab; 